import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import AdminHeader from "components/Headers/AdminHeader";
import { CourseHeaderCard } from "./EnrollCard";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import { formatText } from "components/CommonComponents/CommonFunctions";
import PolicyModal from "./PolicyModal";
import DOMPurify from "dompurify";
import CommonModal from "../../../components/CommonComponents/CommonModal";
import {
  getCourseById,
  getCourseBatchDetailByID,
  EnrolledCourse,
  CourseEnrollmentPayment,
} from "action/course";
import PrerequisiteDocumentsTab from "pages/ChannelPartner/PrerequisiteDocumentsTab";

// Allowed file types
// const ALLOWED_FILE_EXTENSIONS = [
//   ".jpg",
//   ".jpeg",
//   ".png",
//   ".gif",
//   ".webp",
//   ".bmp",
//   ".svg",
//   ".pdf",
// ];

// const ALLOWED_MIME_TYPES = [
//   "image/jpeg",
//   "image/png",
//   "image/gif",
//   "image/webp",
//   "image/bmp",
//   "image/svg+xml",
//   "application/pdf",
// ];

// Read More/Less Component for long text
const ReadMoreText = ({ text, maxLength = 300, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text) return null;

  const isLong = text.length > maxLength;
  const displayText =
    isLong && !isExpanded ? text.slice(0, maxLength) + "..." : text;

  return (
    <div className={className}>
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(displayText),
        }}
      />

      {isLong && (
        <div className="text-center mt-2">
          <button
            type="button"
            className="lesson-read-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? "Read Less" : "Read More"}
          </button>
        </div>
      )}
    </div>
  );
};

// Static trainee data with multiple document statuses
const generateStaticTrainees = () => {
  const statuses = ["ACTIVE", "INACTIVE", "PENDING"];
  const names = [
    "Rohan Sharma",
    "Priya Patel",
    "Amit Kumar",
    "Sneha Reddy",
    "Vikram Singh",
    "Neha Gupta",
    "Arjun Mehta",
    "Kavya Nair",
    "Rajesh Kumar",
    "Ananya Das",
  ];

  const trainees = [];
  for (let i = 1; i <= 25; i++) {
    const name = names[i % names.length];
    const status = statuses[i % statuses.length];
    trainees.push({
      id: i,
      jointerId: `JT-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      name: `${name} ${i > 10 ? "Jr" : ""}`,
      email: `${name.toLowerCase().replace(" ", "")}${i}@yopmail.com`,
      mobile: `98${String(76543210 + i).substring(0, 8)}`,
      organization: [
        "Scalent Infotech",
        "Tech Solutions",
        "Digital India",
        "Infosys",
        "TCS",
      ][i % 5],
      status: status,
      registeredOn: new Date(Date.now() - i * 86400000).toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
      ),
      documents: {
        "Address Proof": ["APPROVED", "PENDING", "REJECTED", null][i % 4],
        "ID Proof": ["APPROVED", "PENDING", "REJECTED", null][(i + 1) % 4],
        "Educational Certificate": ["APPROVED", "PENDING", "REJECTED", null][
          (i + 2) % 4
        ],
        "Experience Letter": ["APPROVED", "PENDING", "REJECTED", null][
          (i + 3) % 4
        ],
        "Passport Photo": ["APPROVED", "PENDING", "REJECTED", null][
          (i + 2) % 4
        ],
      },
      enrolled: i % 3 === 0,
    });
  }
  return trainees;
};

const EnrollPage = () => {
  const { id } = useParams();
  const courseParam = id?.split("&")[0];
  const batchParam = id?.split("&b=")[1];

  const courseId = courseParam ? urlDecodedId(courseParam) : null;
  const urlBatchId = batchParam ? urlDecodedId(batchParam) : null;

  const navigate = useNavigate();
  const { notify } = useContext(NotificationContext);

  // State declarations
  const [skipUpload, setSkipUpload] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [selectedTraineeIds, setSelectedTraineeIds] = useState([]);
  const [courseEnrollDetails, setCourseEnrollDetails] = useState(null);
  const [loading, setLoading] = useState({
    page: true,
    action: false,
    docUpload: false,
  });
  const [policySheet, setPolicySheet] = useState({
    open: false,
    type: null,
  });
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);
  const [isBatchInitialized, setIsBatchInitialized] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [offlinePaymentModal, setOfflinePaymentModal] = useState({
    open: false,
    data: null,
  });
  const [previewData, setPreviewData] = useState({
    src: "",
    type: "",
    fileName: "",
  });
  const [isBatchInfoModalOpen, setIsBatchInfoModalOpen] = useState(false);
  const [activeBatchInfoTab, setActiveBatchInfoTab] = useState("description");
  const [selectedBatchInfo, setSelectedBatchInfo] = useState(null);
  // NEW: State to track if all selected trainees have approved documents
  const [allSelectedTraineesApproved, setAllSelectedTraineesApproved] =
    useState(false);

  // Trainee management state
  const [trainees, setTrainees] = useState([]);
  const [filteredTrainees, setFilteredTrainees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  // const [itemsPerPage] = useState(10);
  const [popoverOpen, setPopoverOpen] = useState({});

  // Get required documents from course details or use default
  const requiredDocuments =
    courseDetails?.coursePrerequisiteDocument?.length > 0
      ? courseDetails.coursePrerequisiteDocument
      : [];

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading((prev) => ({ ...prev, page: true }));

      await fetchCourseDetails(courseId);
      await fetchCourseBatchDetails(courseId);

      const traineeData = generateStaticTrainees();
      setTrainees(traineeData);
      setFilteredTrainees(traineeData);

      setLoading((prev) => ({ ...prev, page: false }));
    };

    if (courseId) {
      loadData();
    }
  }, [courseId]);

  // Filter trainees
  useEffect(() => {
    let filtered = trainees;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.jointerId.toLowerCase().includes(term) ||
          t.name.toLowerCase().includes(term) ||
          t.email.toLowerCase().includes(term) ||
          t.mobile.includes(term) ||
          t.organization.toLowerCase().includes(term),
      );
    }

    if (statusFilter !== "ALL") {
      filtered = filtered.filter((t) => t.status === statusFilter);
    }

    setFilteredTrainees(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, trainees]);

  // API Functions
  const fetchCourseDetails = async (courseId) => {
    try {
      const res = await getCourseById(courseId);
      if (res?.code === 200) {
        setCourseDetails(res.data);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch course details.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
    }
  };

  const fetchCourseBatchDetails = async (courseId) => {
    try {
      const res = await getCourseBatchDetailByID(courseId);
      if (res?.code === 200) {
        setCourseEnrollDetails(res.data);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to fetch batch details.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
    }
  };

  // Get languages and batches
  const languages =
    courseEnrollDetails?.languages?.map((lang) => formatText(lang)) || [];
  const batches = useMemo(() => {
    return selectedLanguage === "English"
      ? courseEnrollDetails?.EnglishBatches || []
      : selectedLanguage === "Hindi"
        ? courseEnrollDetails?.HindiBatches || []
        : [];
  }, [selectedLanguage, courseEnrollDetails]);

  useEffect(() => {
    if (urlBatchId) {
      return;
    }

    if (batches.length === 1) {
      setSelectedBatch(batches[0].ID);
    }
  }, [batches, urlBatchId]);

  // Auto-select language
  useEffect(() => {
    if (!isLanguageInitialized && languages.length > 0) {
      if (languages.includes("English")) {
        setSelectedLanguage("English");
      } else {
        setSelectedLanguage(languages[0]);
      }
      setIsLanguageInitialized(true);
    }
  }, [languages, isLanguageInitialized]);

  useEffect(() => {
    if (!urlBatchId || !courseEnrollDetails || isBatchInitialized) {
      return;
    }

    const englishBatch = courseEnrollDetails?.EnglishBatches?.find(
      (batch) => String(batch.ID) === String(urlBatchId),
    );

    const hindiBatch = courseEnrollDetails?.HindiBatches?.find(
      (batch) => String(batch.ID) === String(urlBatchId),
    );

    if (englishBatch) {
      setSelectedLanguage("English");
      setSelectedBatch(englishBatch.ID);
    } else if (hindiBatch) {
      setSelectedLanguage("Hindi");
      setSelectedBatch(hindiBatch.ID);
    }

    setIsBatchInitialized(true);
  }, [urlBatchId, courseEnrollDetails, isBatchInitialized]);

  // Document status helpers
  const traineeDocuments =
    courseEnrollDetails?.TraineePrerequisiteDocuments || [];

  const getTraineeDocument = (courseDocumentName) => {
    if (!courseDocumentName) return null;
    const target = courseDocumentName.trim().toLowerCase();
    return (
      traineeDocuments.find(
        (td) => td.document_name?.trim().toLowerCase() === target,
      ) || null
    );
  };

  const needsDocumentUpload = (doc) => {
    const traineeDoc = getTraineeDocument(doc.courseDocumentName);
    if (!traineeDoc) return true;
    return traineeDoc.status !== "APPROVED" && traineeDoc.status !== "PENDING";
  };

  const docsNeedingUpload = requiredDocuments.filter(needsDocumentUpload);
  // const hasPendingUploads = docsNeedingUpload.length > 0;

  // const allDocumentsApproved = requiredDocuments.every((doc) => {
  //   const traineeDoc = getTraineeDocument(doc.courseDocumentName);
  //   if (!traineeDoc) return false;
  //   return traineeDoc.status === "APPROVED";
  // });

  // const canSubmitDocuments =
  //   hasPendingUploads && Object.keys(uploadedFiles).length > 0;

  // UPDATED: Main canContinue function - now requires all selected trainees to have approved documents
  const canContinue =
    skipUpload &&
    selectedBatch &&
    selectedLanguage &&
    selectedTraineeIds.length > 0 &&
    allSelectedTraineesApproved; // NEW: Check if all selected trainees have approved documents

  // Status badge helpers
  // const getDocStatusBadge = (status) => {
  //   const map = {
  //     APPROVED: {
  //       color: "success",
  //       text: "Approved",
  //       icon: "fa-check-circle",
  //       class: "status-approved",
  //     },
  //     PENDING: {
  //       color: "warning",
  //       text: "Approval Pending",
  //       icon: "fa-clock",
  //       class: "status-pending",
  //     },
  //     REJECTED: {
  //       color: "danger",
  //       text: "Rejected",
  //       icon: "fa-times-circle",
  //       class: "status-rejected",
  //     },
  //     DELETED: {
  //       color: "danger",
  //       text: "Deleted",
  //       icon: "fa-trash",
  //       class: "status-deleted",
  //     },
  //   };
  //   return (
  //     map[status] || {
  //       color: "secondary",
  //       text: "Not Uploaded",
  //       icon: "fa-minus-circle",
  //       class: "status-not-uploaded",
  //     }
  //   );
  // };

  // const getStatusBadge = (status) => {
  //   const map = {
  //     ACTIVE: { color: "success", icon: "fa-check-circle" },
  //     INACTIVE: { color: "secondary", icon: "fa-minus-circle" },
  //     PENDING: { color: "warning", icon: "fa-clock" },
  //   };
  //   return map[status] || map.INACTIVE;
  // };

  // File validation
  // const isValidFileType = (file) => {
  //   const fileExtension = "." + file.name.split(".").pop().toLowerCase();
  //   return (
  //     ALLOWED_MIME_TYPES.includes(file.type) ||
  //     ALLOWED_FILE_EXTENSIONS.includes(fileExtension)
  //   );
  // };

  // Handle file selection for upload
  // const handleFileSelect = (traineeId, documentName, file) => {
  //   if (!isValidFileType(file)) {
  //     const allowedExtensions = ALLOWED_FILE_EXTENSIONS.join(", ");
  //     notify(
  //       "danger",
  //       `Invalid file type. Please upload only ${allowedExtensions} files.`,
  //     );
  //     // Clear the input
  //     const input = document.getElementById(
  //       `file-input-${traineeId}-${documentName}`,
  //     );
  //     if (input) {
  //       input.value = "";
  //     }
  //     return;
  //   }

  //   // Store the file in state
  //   setUploadedFiles((prev) => ({
  //     ...prev,
  //     [`${traineeId}-${documentName}`]: file,
  //   }));
  // };

  // Handle document preview
  // const handleDocumentPreview = (
  //   e,
  //   docName,
  //   traineeName,
  //   file = null,
  //   docPath = null,
  // ) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   if (file) {
  //     const url = URL.createObjectURL(file);
  //     const type = file.type;
  //     setPreviewData({ src: url, type: type, fileName: file.name });
  //   } else if (docPath) {
  //     // For existing documents from backend
  //     setPreviewData({ src: docPath, type: "", fileName: docName });
  //   } else {
  //     notify("info", `Previewing "${docName}" for ${traineeName}`);
  //   }
  // };

  // Format time from "12:00" to "12:00 PM"
  const formatTime = (time) => {
    if (!time) return "-";
    try {
      const [hours, minutes] = time.split(":");
      const h = parseInt(hours);
      // const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${minutes}`;
    } catch {
      return time;
    }
  };

  // Handle submit documents for a specific trainee
  // const handleSubmitDocuments = async (traineeId) => {
  //   // Get all files for this trainee
  //   const traineeFiles = Object.keys(uploadedFiles).filter((key) =>
  //     key.startsWith(`${traineeId}-`),
  //   );

  //   if (traineeFiles.length === 0) {
  //     notify("warning", "No documents selected to upload.");
  //     return;
  //   }

  //   setUploadingDocs((prev) => ({ ...prev, [traineeId]: true }));

  //   try {
  //     const formData = new FormData();
  //     let hasFiles = false;

  //     traineeFiles.forEach((key) => {
  //       const file = uploadedFiles[key];
  //       const documentName = key.split("-")[1];
  //       const doc = requiredDocuments.find(
  //         (d) => d.courseDocumentName === documentName,
  //       );

  //       if (file && doc) {
  //         const traineeDocId = doc.courseDocumentID;
  //         formData.append("documentId", traineeDocId);
  //         formData.append("document", file, file.name);
  //         hasFiles = true;
  //       }
  //     });

  //     if (!hasFiles) {
  //       notify("warning", "No valid files to upload.");
  //       setUploadingDocs((prev) => ({ ...prev, [traineeId]: false }));
  //       return;
  //     }

  //     // Call the API - uncomment when ready
  //     // const res = await uploadTraineeDocuments(formData);

  //     // if (res?.code === 200 || res?.status === "SUCCESS") {
  //     //   notify("success", res?.message || "Documents uploaded successfully.");

  //     //   // Update trainee document statuses
  //     //   const updatedTrainees = trainees.map(t => {
  //     //     if (t.id === traineeId) {
  //     //       const updatedDocs = { ...t.documents };
  //     //       traineeFiles.forEach(key => {
  //     //         const docName = key.split('-')[1];
  //     //         updatedDocs[docName] = 'PENDING';
  //     //       });
  //     //       return { ...t, documents: updatedDocs };
  //     //     }
  //     //     return t;
  //     //   });
  //     //   setTrainees(updatedTrainees);

  //     //   // Clear uploaded files for this trainee
  //     //   const remainingFiles = { ...uploadedFiles };
  //     //   traineeFiles.forEach(key => {
  //     //     delete remainingFiles[key];
  //     //   });
  //     //   setUploadedFiles(remainingFiles);

  //     //   // Reset file inputs
  //     //   traineeFiles.forEach(key => {
  //     //     const input = document.getElementById(`file-input-${key}`);
  //     //     if (input) input.value = '';
  //     //   });
  //     // } else {
  //     //   const message = Array.isArray(res?.message)
  //     //     ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
  //     //     : res?.message || "Failed to upload documents.";
  //     //   notify("danger", message);
  //     // }
  //   } catch (error) {
  //     notify(
  //       "danger",
  //       error?.response?.data?.message ||
  //       "Something went wrong while uploading documents.",
  //     );
  //   } finally {
  //     setUploadingDocs((prev) => ({ ...prev, [traineeId]: false }));
  //   }
  // };

  // Enrollment functions
  const handleEnroll = async () => {
    if (selectedTraineeIds.length === 0) {
      notify("warning", "Please select at least one trainee.");
      return null;
    }
    let Amount = Number(calculatedPayableAmount?.toFixed(2));
    const payload = {
      courseID: courseId,
      batchID: selectedBatch,
      trainees: selectedTraineeIds,
      amount: Amount,
    };
    const res = await EnrolledCourse(payload);
    return res;
  };

  const handleFreeEnroll = async () => {
    setLoading((prev) => ({ ...prev, action: true }));
    try {
      const res = await handleEnroll();
      if (res?.code === 200) {
        notify("success", res.message || "Course enrolled successfully.");
        navigate("/user/courses");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to enroll.";
        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handlePayment = async () => {
    setLoading((prev) => ({ ...prev, action: true }));
    try {
      const res = await handleEnroll();
      if (res?.code !== 200) {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to enroll.";
        notify("danger", message);
        setLoading((prev) => ({ ...prev, action: false }));
        return;
      }

      if (res?.data?.paymentMode === "OFFLINE") {
        setOfflinePaymentModal({ open: true, data: res.data });
        setLoading((prev) => ({ ...prev, action: false }));
        return;
      }

      if (!window.Razorpay) {
        notify("danger", "Razorpay SDK failed to load.");
        setLoading((prev) => ({ ...prev, action: false }));
        return;
      }

      const { key, amount, orderId, trainee } = res.data;
      const options = {
        key,
        amount: amount,
        currency: "INR",
        name: "TMS",
        description: "Course Enrollment",
        order_id: orderId,
        prefill: {
          name: trainee?.name || "",
          email: trainee?.email || "",
          contact: trainee?.mobileNo || "",
        },
        notes: {
          courseID: courseId,
          batchID: selectedBatch,
        },
        handler: async (response) => {
          setLoading((prev) => ({ ...prev, action: true }));
          try {
            const payload = {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
            };
            const verifyRes = await CourseEnrollmentPayment(payload);
            if (verifyRes?.code === 200) {
              notify("success", verifyRes?.message || "Payment Successful.");
              navigate("/user/courses");
            } else {
              notify("danger", "Payment verification failed.");
            }
          } catch (error) {
            notify(
              "danger",
              error?.response?.data?.message || "Payment verification failed.",
            );
          } finally {
            setLoading((prev) => ({ ...prev, action: false }));
          }
        },
        modal: {
          ondismiss: () => {
            setLoading((prev) => ({ ...prev, action: false }));
            notify("warning", "Payment was cancelled.");
          },
        },
        theme: { color: "#0f4c81" },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        notify("danger", response?.error?.description || "Payment failed.");
        setLoading((prev) => ({ ...prev, action: false }));
      });
      razorpay.open();
    } catch (error) {
      notify(
        "danger",
        error?.response?.data?.message || "Something went wrong.",
      );
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    setSelectedBatch(null);
    setExpandedBatch(null);
  };

  // Pagination
  // const indexOfLastItem = currentPage * itemsPerPage;
  // const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  // const currentItems = filteredTrainees.slice(
  //   indexOfFirstItem,
  //   indexOfLastItem,
  // );
  // const totalPages = Math.ceil(filteredTrainees.length / itemsPerPage);

  // Toggle trainee expansion
  // const toggleTrainee = (traineeId) => {
  //   setExpandedTrainee(expandedTrainee === traineeId ? null : traineeId);
  // };

  const onClosePaymentModal = () => {
    setOfflinePaymentModal({ open: false, data: null });
  };

  // Offline Payment Modal
  const OfflinePaymentModal = ({ isOpen, data, onClose }) => {
    return (
      <Modal
        isOpen={isOpen}
        centered
        size="md"
        className="offline-payment-modal"
      >
        <ModalBody>
          <div className="text-center">
            {/* Success Icon with animation */}
            <div className="offline-success-icon">
              <i className="fa fa-check-circle"></i>
            </div>

            <h4 className="offline-title">Payment Sent for Approval</h4>
            <p className="offline-subtitle">
              Your enrollment request has been submitted successfully. Your
              payment is currently being reviewed by our team.
            </p>
            {/* Info Box */}
            <div className="offline-info-box mt-3">
              <i className="fa fa-info-circle"></i>
              <span>
                You will be notified once your payment is approved. This usually
                takes 24-48 hours.
              </span>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="border-0 pt-0 justify-content-center">
          <Button
            color="primary"
            className="offline-btn"
            onClick={() => {
              onClosePaymentModal();
              navigate("/channel-partner/course");
            }}
          >
            <i className="fa fa-arrow-right"></i>
            Go to Courses
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  // Toggle popover
  const togglePopover = (batchId) => {
    setPopoverOpen((prev) => ({
      ...prev,
      [batchId]: !prev[batchId],
    }));
  };

  const selectedTraineeCount = selectedTraineeIds.length;

  const baseCourseFee = Number(
    courseEnrollDetails?.paymentDetails?.subtotal || 0,
  );

  const gstRate =
    courseEnrollDetails?.paymentDetails?.totalGSTPercentageApplied;

  const calculatedSubtotal = baseCourseFee * selectedTraineeCount;

  const calculatedGst = calculatedSubtotal * (gstRate / 100);

  const calculatedPayableAmount = calculatedSubtotal + calculatedGst;

  // Per-trainee GST amounts from API
  const baseCgstAmount = Number(
    courseEnrollDetails?.paymentDetails?.cgstAmount || 0,
  );
  const baseSgstAmount = Number(
    courseEnrollDetails?.paymentDetails?.sgstAmount || 0,
  );
  const baseIgstAmount = Number(
    courseEnrollDetails?.paymentDetails?.igstAmount || 0,
  );

  // Multiplied GST amounts
  const calculatedCgstAmount = baseCgstAmount * selectedTraineeCount;
  const calculatedSgstAmount = baseSgstAmount * selectedTraineeCount;
  const calculatedIgstAmount = baseIgstAmount * selectedTraineeCount;

  // Percentages (for display)
  const cgstPercentage =
    courseEnrollDetails?.paymentDetails?.cgstPercentage || 0;
  const sgstPercentage =
    courseEnrollDetails?.paymentDetails?.sgstPercentage || 0;
  const igstPercentage =
    courseEnrollDetails?.paymentDetails?.igstPercentage || 0;

  const isBatchFull = (batch) =>
    batch?.capacityStatus?.toUpperCase() === "FULL";

  const hasTrainingCenter = batches.some((batch) =>
    batch.trainingCenters?.name?.trim(),
  );

  return (
    <>
      <AdminHeader parentName="Courses" />
      <Container fluid className="mt--7">
        <div
          className="d-inline-flex align-items-center cursor text-white channelPartner-back-btn"
          onClick={() => navigate(-1)}
        >
          <i className="fa-solid fa-angle-left mr-2" />
          <span className="font-weight-bold">Back</span>
        </div>

        <Row className="mt-4">
          <Col lg="12">
            {loading.page ? (
              <div className="loaderContainer mt-5">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : !courseDetails ? (
              <div className="loaderContainer text-muted d-flex flex-column">
                <h4 className="text-muted">No course details available</h4>
              </div>
            ) : (
              <>
                <CourseHeaderCard
                  courseDetails={courseDetails}
                  showEnrollButton={false}
                  compactImage={true}
                />

                <Row>
                  <Col lg="8">
                    <Card className="overview-card">
                      <CardBody>
                        {/* Language Selection */}
                        <div className="section-header-i">
                          <div className="section-marker"></div>
                          <h4 className="doc-title">Select Language</h4>
                          <i
                            className="fa fa-info-circle info-icon"
                            id="info-language"
                          ></i>
                          <UncontrolledTooltip
                            target="info-language"
                            placement="right"
                          >
                            Choose your preferred language of instruction
                          </UncontrolledTooltip>
                        </div>

                        <div className="language-pills">
                          {languages.map((lang) => (
                            <button
                              type="button"
                              key={lang}
                              className={`language-pill ${selectedLanguage === lang ? "selected" : ""}`}
                              onClick={() => handleLanguageChange(lang)}
                            >
                              <span className="language-icon">
                                <i
                                  className={`fa-solid ${lang === "English" ? "fa-e" : "fa-h"}`}
                                />
                              </span>
                              <span>{lang}</span>
                            </button>
                          ))}
                        </div>

                        {/* Batch Selection */}
                        {selectedLanguage && (
                          <>
                            <div className="section-header-i">
                              <div className="section-marker"></div>
                              <h4 className="doc-title">Select Batch</h4>
                              <i
                                className="fa fa-info-circle info-icon"
                                id="info-batch"
                              ></i>
                              <UncontrolledTooltip
                                target="info-batch"
                                placement="right"
                              >
                                Choose a batch timing that works best for you
                              </UncontrolledTooltip>
                            </div>

                            <Row className="batch-grid">
                              {batches.length > 0 ? (
                                batches.map((batch) => (
                                  <Col
                                    key={batch.ID}
                                    md="6"
                                    sm="12"
                                    className="mb-3"
                                  >
                                    <div
                                      className={`channelPartner-batch-card ${
                                        !hasTrainingCenter
                                          ? "batch-card-small"
                                          : ""
                                      } ${selectedBatch === batch.ID ? "selected" : ""} ${
                                        isBatchFull(batch)
                                          ? "batch-full-disabled"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        if (isBatchFull(batch)) return; // 🚫 block selection
                                        setSelectedBatch(batch.ID);
                                      }}
                                      title={
                                        isBatchFull(batch)
                                          ? "This batch is full"
                                          : ""
                                      }
                                      aria-disabled={isBatchFull(batch)}
                                    >
                                      {/* ✅ ADD: Full badge at top */}
                                      {isBatchFull(batch) && (
                                        <span className="batch-full-badge">
                                          <i className="fa fa-ban mr-1"></i>{" "}
                                          FULL
                                        </span>
                                      )}

                                      <div className="channelPartner-batch-card-top">
                                        <div className="feature-icon">
                                          <i className="fa fa-clock"></i>
                                        </div>

                                        <div className="feature-info">
                                          <h4
                                            className="feature-label  "
                                            title={batch.name}
                                          >
                                            {batch.name}
                                          </h4>
                                          <span
                                            className="feature-value"
                                            title={`${batch.batchSession} • ${formatTime(batch.startTime)} - ${formatTime(batch.endTime)}`}
                                          >
                                            {batch.batchSession} •{" "}
                                            {formatTime(batch.startTime)} -{" "}
                                            {formatTime(batch.endTime)}
                                          </span>
                                        </div>
                                      </div>

                                      {/* ✅ FIX: only show check if selected AND not full */}
                                      {selectedBatch === batch.ID &&
                                        !isBatchFull(batch) && (
                                          <i className="fa fa-check-circle batch-check"></i>
                                        )}

                                      <div className="batch-footer">
                                        <div className="batch-footer-top">
                                          <div className="batch-seats-wrapper">
                                            {batch.availableSeats !== "NA" &&
                                              batch.availableSeats && (
                                                <span className="batch-seats">
                                                  <i className="fa fa-users"></i>
                                                  {isBatchFull(batch)
                                                    ? "No seats available"
                                                    : `${batch.availableSeats} seats left`}
                                                </span>
                                              )}
                                          </div>
                                          <div className="batch-description-wrapper">
                                            {(batch.description ||
                                              batch.scheduleInfo?.trim()) && (
                                              <i
                                                className="fa fa-info-circle info-popover-icon batch-description-icon"
                                                title="View batch information"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setSelectedBatchInfo(batch);
                                                  setActiveBatchInfoTab(
                                                    "description",
                                                  );
                                                  setIsBatchInfoModalOpen(true);
                                                }}
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div className="batch-footer-bottom ">
                                          <div className="batch-location">
                                            {batch.trainingCenters?.name && (
                                              <>
                                                <i className="fa fa-map-marker"></i>
                                                {batch.trainingCenters.name}
                                              </>
                                            )}
                                          </div>
                                          <div></div>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                ))
                              ) : (
                                <Col xs="12">
                                  <div className="text-start p-2">
                                    <h5 className="text-muted">
                                      No batches are currently available for the
                                      selected language.
                                    </h5>
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </>
                        )}

                        {/* Required Documents with Trainee Accordion */}
                        <div className="section-header-i mt-4">
                          <div className="section-marker"></div>
                          <h4 className="doc-title">
                            {requiredDocuments.length > 0
                              ? "Required Documents"
                              : "Select Trainees"}
                          </h4>
                          <i
                            className="fa fa-info-circle info-icon"
                            id="info-docs"
                          ></i>
                          <UncontrolledTooltip
                            target="info-docs"
                            placement="right"
                          >
                            {requiredDocuments.length > 0
                              ? "Click on trainee name to view and manage their documents"
                              : "Select the trainees to enroll in this course"}
                          </UncontrolledTooltip>
                        </div>

                        <PrerequisiteDocumentsTab
                          courseId={courseId}
                          requiredDocuments={requiredDocuments}
                          onSelectedTraineesChange={setSelectedTraineeIds}
                          onSelectedTraineesStatusChange={
                            setAllSelectedTraineesApproved
                          }
                        />

                        {/* Additional Notes */}
                        {courseDetails?.notes?.trim() && (
                          <>
                            <div className="section-header-i mt-4">
                              <div className="section-marker"></div>
                              <h4 className="doc-title">Additional Notes</h4>
                              <i
                                className="fa fa-info-circle info-icon"
                                id="info-notes"
                              ></i>
                              <UncontrolledTooltip
                                target="info-notes"
                                placement="right"
                              >
                                Please read these instructions before
                                continuing.
                              </UncontrolledTooltip>
                            </div>
                            <div className="course-notes-box">
                              <ReadMoreText
                                text={courseDetails.notes}
                                maxLength={200}
                                className="mb-0 text-muted"
                              />
                            </div>
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="4">
                    <Card className="card-enroll">
                      <CardBody>
                        {courseDetails?.accessType !== "FREE" && (
                          <>
                            <div className="section-header-i">
                              <div className="section-marker"></div>
                              <h4 className="doc-title">Payment Summary</h4>
                              <i
                                className="fa fa-info-circle info-icon"
                                id="info-payment"
                              ></i>
                              <UncontrolledTooltip
                                target="info-payment"
                                placement="right"
                              >
                                Review your course fee and tax breakdown before
                                proceeding.
                              </UncontrolledTooltip>
                            </div>

                            <p className="gst-note text-muted">
                              18% GST will be applied to this course fee.
                            </p>

                            <Card className="payment-card mb-3">
                              <CardBody className="payment-card-body">
                                <div
                                  className="payment-summary-header cursor"
                                  onClick={() =>
                                    setShowPaymentSummary(!showPaymentSummary)
                                  }
                                >
                                  <div>
                                    <span className="payment-subtitle">
                                      Total Amount
                                    </span>
                                  </div>
                                  <div className="payment-header-right">
                                    <h4 className="payment-total mb-0">
                                      ₹{calculatedPayableAmount.toFixed(2)}
                                    </h4>
                                    <span className="lesson-arrow ml-2">
                                      <i
                                        className={`fas fa-chevron-${showPaymentSummary ? "up" : "down"}`}
                                      />
                                    </span>
                                  </div>
                                </div>

                                {showPaymentSummary && (
                                  <div className="payment-summary-content">
                                    {/* Course Fee */}
                                    <div className="summary-row">
                                      <span>
                                        Course Fee ({selectedTraineeCount}{" "}
                                        {selectedTraineeCount === 1
                                          ? "Trainee"
                                          : "Trainees"}
                                        )
                                      </span>
                                      <strong>
                                        ₹{calculatedSubtotal.toFixed(2)}
                                      </strong>
                                    </div>

                                    {/* CGST */}
                                    {baseCgstAmount > 0 && (
                                      <div className="summary-row">
                                        <span>CGST ({cgstPercentage}%)</span>
                                        <strong>
                                          ₹{calculatedCgstAmount.toFixed(2)}
                                        </strong>
                                      </div>
                                    )}

                                    {/* SGST */}
                                    {baseSgstAmount > 0 && (
                                      <div className="summary-row">
                                        <span>SGST ({sgstPercentage}%)</span>
                                        <strong>
                                          ₹{calculatedSgstAmount.toFixed(2)}
                                        </strong>
                                      </div>
                                    )}

                                    {/* IGST */}
                                    {baseIgstAmount > 0 && (
                                      <div className="summary-row">
                                        <span>IGST ({igstPercentage}%)</span>
                                        <strong>
                                          ₹{calculatedIgstAmount.toFixed(2)}
                                        </strong>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </>
                        )}

                        {/* Terms */}
                        <div className="skip-option mb-4">
                          <label className="custom-checkbox">
                            <input
                              type="checkbox"
                              checked={skipUpload}
                              onChange={(e) => setSkipUpload(e.target.checked)}
                            />
                            <span className="checkmark"></span>
                            <span className="checkbox-label">
                              I agree to the{" "}
                              <span
                                className="span-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPolicySheet({ open: true, type: "terms" });
                                }}
                              >
                                Terms & Conditions
                              </span>{" "}
                              and{" "}
                              <span
                                className="span-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPolicySheet({
                                    open: true,
                                    type: "privacy",
                                  });
                                }}
                              >
                                Privacy Policy
                              </span>
                            </span>
                          </label>
                        </div>

                        {/* UPDATED: Show status message about document approval
                        {selectedTraineeIds.length > 0 && requiredDocuments.length > 0 && (
                          <div className={`doc-status-message mb-3 ${allSelectedTraineesApproved ? 'text-success' : 'text-warning'}`}>
                            <i className={`fas ${allSelectedTraineesApproved ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                            {allSelectedTraineesApproved ? (
                              <span>All selected trainees have approved documents. Ready to enroll!</span>
                            ) : (
                              <span>Some selected trainees have pending or rejected documents. Please ensure all documents are approved before enrolling.</span>
                            )}
                          </div>
                        )} */}

                        <Button
                          size="lg"
                          block
                          className={`continue-btn ${!canContinue ? "disabled" : ""}`}
                          disabled={!canContinue || loading.action}
                          onClick={() => {
                            if (courseDetails?.accessType === "FREE") {
                              handleFreeEnroll();
                            } else {
                              handlePayment();
                            }
                          }}
                        >
                          {loading.action ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-2"></i>
                              Processing...
                            </>
                          ) : (
                            <>
                              {courseDetails?.accessType === "FREE"
                                ? "Continue to Enroll"
                                : "Request enrollment approval"}
                              <i className="fa fa-arrow-right ml-2"></i>
                            </>
                          )}
                        </Button>

                        {/* UPDATED: Continue hint with document approval check */}
                        {!canContinue && (
                          <div className="continue-hint mt-3">
                            <i className="fa fa-info-circle mr-2"></i>
                            {!selectedLanguage
                              ? "Select a language to continue."
                              : !selectedBatch
                                ? "Select a batch to continue."
                                : selectedTraineeIds.length === 0
                                  ? "Select at least one trainee to continue."
                                  : !allSelectedTraineesApproved
                                    ? "All selected trainees must have all documents approved before enrolling."
                                    : !skipUpload
                                      ? "Accept the Terms & Conditions to continue."
                                      : "Please complete all required selections to continue."}
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>

      {/* Offline Payment Modal */}
      <OfflinePaymentModal
        isOpen={offlinePaymentModal.open}
        data={offlinePaymentModal.data}
        onClose={() => setOfflinePaymentModal({ open: false, data: null })}
      />

      <PolicyModal
        isOpen={policySheet.open}
        type={policySheet.type}
        onClose={() => setPolicySheet({ open: false, type: "terms" })}
      />

      <CommonModal
        isOpen={!!previewData.src}
        toggle={() => setPreviewData({ src: "", type: "", fileName: "" })}
        title={previewData.fileName || "Document Preview"}
        size="xl"
        centered={true}
        height="80vh"
      >
        <div style={{ width: "100%", height: "calc(80vh - 100px)" }}>
          {previewData.src && previewData.type === "application/pdf" ? (
            <iframe
              src={previewData.src}
              title="PDF Document Preview"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div
              className="text-center"
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "auto",
              }}
            >
              <img
                src={previewData.src}
                alt="Document Preview"
                className="img-fluid image-preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            </div>
          )}
        </div>
      </CommonModal>

      <CommonModal
        isOpen={isBatchInfoModalOpen}
        toggle={() => setIsBatchInfoModalOpen(false)}
        title={
          selectedBatchInfo?.scheduleInfo?.trim()
            ? "Batch Information"
            : " Batch Details "
        }
        size="lg"
      >
        {selectedBatchInfo?.description && selectedBatchInfo?.scheduleInfo ? (
          <>
            <Nav className="custom-tabs cursor">
              <NavItem>
                <NavLink
                  className={
                    activeBatchInfoTab === "description" ? "active" : ""
                  }
                  onClick={() => setActiveBatchInfoTab("description")}
                >
                  Batch Description
                </NavLink>
              </NavItem>

              {selectedBatchInfo?.scheduleInfo?.trim() && (
                <NavItem>
                  <NavLink
                    className={
                      activeBatchInfoTab === "schedule" ? "active" : ""
                    }
                    onClick={() => setActiveBatchInfoTab("schedule")}
                  >
                    Schedule Information
                  </NavLink>
                </NavItem>
              )}
            </Nav>
            <TabContent
              activeTab={activeBatchInfoTab}
              className="batch-info-tab-content"
            >
              <TabPane tabId="description">
                <div className="batch-info-content">
                  {selectedBatchInfo?.description ? (
                    <ReadMoreText
                      text={selectedBatchInfo.description}
                      maxLength={200}
                      className="popover-text channelPartner-desc-text"
                    />
                  ) : (
                    "No batch description available."
                  )}
                </div>
              </TabPane>

              {selectedBatchInfo?.scheduleInfo?.trim() && (
                <TabPane tabId="schedule">
                  <div className="batch-info-content">
                    <ReadMoreText
                      text={selectedBatchInfo.scheduleInfo}
                      maxLength={200}
                      className="popover-text channelPartner-desc-text"
                    />
                  </div>
                </TabPane>
              )}
            </TabContent>
          </>
        ) : (
          <div>
            {selectedBatchInfo?.description ? (
              <ReadMoreText
                text={selectedBatchInfo.description}
                maxLength={200}
                className="popover-text channelPartner-desc-text"
              />
            ) : (
              "No batch description available."
            )}
          </div>
        )}
      </CommonModal>
    </>
  );
};

export default EnrollPage;
